import React, { useEffect, useMemo, useState } from "react";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetActivitiesQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  type Activity,
  type CreateActivityRequest,
} from "../features/activities/apiSlice";
import { useGetDevicesByProjectQuery } from "../features/devices/apiSlice";
import EmptyHint from "../components/EmptyHint";
import dayjs from "dayjs";

interface ActivityForm {
  activity_name: string;
  from_date: string;
  to_date: string;
  notes: string;
  device_ids: string[];
}

const EMPTY_FORM: ActivityForm = {
  activity_name: "",
  from_date: "",
  to_date: "",
  notes: "",
  device_ids: [],
};

const Activities: React.FC = () => {
  const { projectId } = useProjectContext();
  const [search, setSearch] = useState<string>("");
  const [checkpointsFilter, setCheckpointsFilter] = useState<string>("");
  const [fromDateFilter, setFromDateFilter] = useState<string>(
    dayjs().format("YYYY-MM-DD"),
  );
  const [toDateFilter, setToDateFilter] = useState<string>(
    dayjs().format("YYYY-MM-DD"),
  );

  const { data: activities, isFetching } = useGetActivitiesQuery(
    { project_id: projectId ?? undefined },
    { skip: projectId == null },
  );

  useEffect(() => {
    const dates = (activities || [])
      .reduce((acc: string[], a) => {
        const fromDate = dayjs(a.from_date).format("YYYY-MM-DD");
        const toDate = dayjs(a.to_date).format("YYYY-MM-DD");
        acc.push(fromDate);
        acc.push(toDate);
        return acc;
      }, [])
      .sort((a, b) => dayjs(a).unix() - dayjs(b).unix());
    setFromDateFilter(dates.at(0) ?? "");
    setToDateFilter(dates.at(-1) ?? "");
  }, [activities]);

  const { data: devices } = useGetDevicesByProjectQuery(projectId as number, {
    skip: projectId == null,
  });

  const [createActivity, { isLoading: creating }] = useCreateActivityMutation();
  const [updateActivity, { isLoading: updating }] = useUpdateActivityMutation();
  const [deleteActivity] = useDeleteActivityMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ActivityForm>(EMPTY_FORM);
  const [copiedDeviceIds, setCopiedDeviceIds] = useState<string[] | null>(null);
  const [copyFlash, setCopyFlash] = useState<number | null>(null);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (a: Activity) => {
    setEditId(a.id);
    setForm({
      activity_name: a.activity_name,
      from_date: dayjs(a.from_date ?? "").format("YYYY-MM-DD") ?? "",
      to_date: dayjs(a.to_date ?? "").format("YYYY-MM-DD") ?? "",
      notes: a.notes ?? "",
      device_ids: a.devices.map((d) => d.id),
    });
    setModalOpen(true);
  };

  const copyDevices = (a: Activity) => {
    const ids = a.devices.map((d) => d.id);
    setCopiedDeviceIds(ids);
    setCopyFlash(a.id);
    setTimeout(() => setCopyFlash(null), 1200);
  };

  const pasteDevices = () => {
    if (copiedDeviceIds == null) return;
    setForm((f) => ({ ...f, device_ids: copiedDeviceIds }));
  };

  const selectAllDevices = () => {
    setForm((f) => ({
      ...f,
      device_ids: (devices ?? []).map((device) => device.id),
    }));
  };

  const clearDevices = () => {
    setForm((f) => ({ ...f, device_ids: [] }));
  };

  const toggleDevice = (id: string) => {
    setForm((f) => ({
      ...f,
      device_ids: f.device_ids.includes(id)
        ? f.device_ids.filter((x) => x !== id)
        : [...f.device_ids, id],
    }));
  };

  const canSave =
    form.activity_name.trim() !== "" &&
    form.from_date !== "" &&
    form.to_date !== "" &&
    form.to_date >= form.from_date;

  const handleSave = async () => {
    if (!canSave || projectId == null) return;
    const payload: CreateActivityRequest = {
      project_id: projectId,
      activity_name: form.activity_name.trim(),
      from_date: form.from_date,
      to_date: form.to_date,
      notes: form.notes.trim() || undefined,
      device_ids: form.device_ids,
    };
    try {
      if (editId == null) {
        await createActivity(payload).unwrap();
      } else {
        await updateActivity({ id: editId, body: payload }).unwrap();
      }
      setModalOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Failed to save activity.");
    }
  };

  const handleDelete = async (a: Activity) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete activity "${a.activity_name}"?`)) return;
    try {
      await deleteActivity(a.id).unwrap();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Failed to delete activity.");
    }
  };

  const filtered = useMemo(
    () =>
      (activities ?? []).filter((a) => {
        // Search filter
        if (search.trim()) {
          const q = search.toLowerCase();
          if (
            !a.activity_name.toLowerCase().includes(q) &&
            !(a.notes ?? "").toLowerCase().includes(q)
          )
            return false;
        }
        // Checkpoints (devices) filter - search by device names
        if (checkpointsFilter.trim()) {
          const terms = checkpointsFilter
            .toLowerCase()
            .split(/[,\s]+/)
            .filter((t) => t.length > 0);

          if (terms.length > 0) {
            const deviceNames = a.devices.map((d) =>
              d.device_name.toLowerCase(),
            );
            const hasMatch = terms.some((term) =>
              deviceNames.some((name) => name.includes(term)),
            );
            if (!hasMatch) return false;
          }
        }
        // From date filter
        if (
          fromDateFilter &&
          a.from_date >= fromDateFilter &&
          toDateFilter &&
          a.from_date <= toDateFilter
        )
          return true;
        // To date filter
        if (
          fromDateFilter &&
          a.to_date >= fromDateFilter &&
          toDateFilter &&
          a.to_date <= toDateFilter
        )
          return true;
        return false;
      }),
    [activities, search, checkpointsFilter, fromDateFilter, toDateFilter],
  );

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to load activities."
      />
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Activities</h1>
          <div className="sub">
            {filtered.length}{" "}
            {filtered.length === 1 ? "activity" : "activities"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <i className="bi bi-plus-lg" /> New activity
        </button>
      </div>

      <div className="card mb-4">
        <div className="card-head">
          <div className="row gap-2">
            <input
              className="input"
              placeholder="Search activity..."
              style={{ width: 180 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="input"
              placeholder="Checkpoint names..."
              style={{ width: 180 }}
              value={checkpointsFilter}
              onChange={(e) => setCheckpointsFilter(e.target.value)}
            />
            <input
              className="input"
              type="date"
              placeholder="From date"
              style={{ width: 150 }}
              value={fromDateFilter}
              onChange={(e) => setFromDateFilter(e.target.value)}
            />
            <input
              className="input"
              type="date"
              placeholder="To date"
              style={{ width: 150 }}
              value={toDateFilter}
              onChange={(e) => setToDateFilter(e.target.value)}
            />
          </div>
        </div>
        {isFetching ? (
          <EmptyHint
            icon="bi-hourglass-split"
            title="Loading…"
            subtitle="Fetching activities from API."
          />
        ) : filtered.length === 0 ? (
          <EmptyHint
            icon="bi-calendar-event"
            title="No activities"
            subtitle="No activities match the current filters."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Date Range</th>
                <th>Checkpoints</th>
                <th style={{ width: 160, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className="fw-600">{a.activity_name}</div>
                    {a.notes && (
                      <div className="text-muted text-sm">{a.notes}</div>
                    )}
                  </td>
                  <td>
                    <div className="text-sm">
                      {a.from_date} - {a.to_date}
                    </div>
                  </td>
                  <td>
                    {a.devices.length === 0 ? (
                      <span className="text-muted text-sm">No devices</span>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {a.devices.map((d) => (
                          <span key={d.id} className="badge ok">
                            {d.device_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      className="btn btn-sm"
                      onClick={() => copyDevices(a)}
                      title="Copy Checkpoints"
                      style={{ marginRight: 4 }}
                    >
                      <i
                        className={`bi ${copyFlash === a.id ? "bi-clipboard-check" : "bi-clipboard"}`}
                      />
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => openEdit(a)}
                      title="Edit"
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(a)}
                      style={{ marginLeft: 8 }}
                      title="Delete"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <style>{`
        .device-pick { display:flex; flex-wrap:wrap; gap:8px; max-height:220px; overflow:auto; }
        .device-pick .chip { cursor:pointer; user-select:none; }
        .device-pick .chip.on { background: var(--c-primary); color:#fff; border-color: var(--c-primary); }
      `}</style>

      {modalOpen && (
        <div
          className="modal-backdrop show"
          onClick={() => setModalOpen(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{editId == null ? "New activity" : "Edit activity"}</h3>
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => setModalOpen(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="modal-body">
              <div className="field">
                <label>Activity name</label>
                <input
                  className="input"
                  value={form.activity_name}
                  onChange={(e) =>
                    setForm({ ...form, activity_name: e.target.value })
                  }
                  placeholder="e.g. Excavation stage 2"
                />
              </div>
              <div className="grid cols-2">
                <div className="field">
                  <label>From date</label>
                  <input
                    className="input"
                    type="date"
                    value={form.from_date}
                    onChange={(e) =>
                      setForm({ ...form, from_date: e.target.value })
                    }
                  />
                </div>
                <div className="field">
                  <label>To date</label>
                  <input
                    className="input"
                    type="date"
                    value={form.to_date}
                    onChange={(e) =>
                      setForm({ ...form, to_date: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="field">
                <label>Notes</label>
                <textarea
                  className="textarea"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes…"
                />
              </div>
              <div className="field">
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span>
                    Linked Checkpoints ({form.device_ids.length} selected)
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={selectAllDevices}
                    disabled={(devices ?? []).length === 0}
                    style={{ marginLeft: "auto" }}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={clearDevices}
                    disabled={form.device_ids.length === 0}
                  >
                    Unselect all
                  </button>
                  {copiedDeviceIds != null && (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={pasteDevices}
                      title={`Paste ${copiedDeviceIds.length} copied device(s)`}
                    >
                      <i className="bi bi-clipboard-plus" /> Paste devices (
                      {copiedDeviceIds.length})
                    </button>
                  )}
                </label>
                <div className="device-pick">
                  {(devices ?? []).length === 0 && (
                    <span className="text-muted text-sm">
                      No Checkpoints in this project.
                    </span>
                  )}
                  {(devices ?? []).map((d) => (
                    <span
                      key={d.id}
                      className={`badge muted chip ${
                        form.device_ids.includes(d.id) ? "on" : ""
                      }`}
                      onClick={() => toggleDevice(d.id)}
                    >
                      {d.device_name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-foot">
              <button
                className="btn btn-ghost"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={!canSave || creating || updating}
                onClick={handleSave}
              >
                {creating || updating ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Activities;
